import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Trash2, PlusCircle, UserCog } from 'lucide-react';
import AddLLMConfigModal from './llm/AddLLMConfigModal';
import { llmConfigurationService, type LLMConfiguration } from '../../../components/api/llmConfigurationService';
import { apiService } from '../../../components/api/apiService';
import PermissionsAssignmentModal from '../common/Permissions/PermissionsAssignmentModal';

const LLMs = () => {
  const [showModal, setShowModal] = useState(false);
  const [editConfig, setEditConfig] = useState<LLMConfiguration | null>(null);
  const [llmsList, setLlmsList] = useState<LLMConfiguration[]>([]);
  const [providerNames, setProviderNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toggleStatusConfig, setToggleStatusConfig] = useState<LLMConfiguration | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [showAssignmentModal, setAssignmentModal] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const list = await llmConfigurationService.getList();
      setLlmsList(Array.isArray(list) ? list : []);
    } catch (err) {
      setListError('Failed to load LLM configurations.');
      setLlmsList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const loadProviderNames = async () => {
      try {
        const data = await apiService.get('/api/LLMProviderType/GetAll', {}, 'backend');
        const list = Array.isArray(data) ? data : (data?.data ?? data ?? []);
        const map: Record<string, string> = {};
        list.forEach((p: { id: string; name: string }) => { map[p.id] = p.name; });
        setProviderNames(map);
      } catch {
        setProviderNames({});
      }
    };
    loadProviderNames();
  }, []);

  const getDisplayName = (llm: LLMConfiguration) => {
    if (llm.additionalConfigJson) {
      try {
        const o = JSON.parse(llm.additionalConfigJson);
        if (o?.displayName) return o.displayName;
      } catch {}
    }
    return llm.modelName || llm.id;
  };

  const handleEdit = (llm: LLMConfiguration) => {
    setEditConfig(llm);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditConfig(null);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await llmConfigurationService.delete(deleteId);
      await fetchList();
      setDeleteId(null);
    } catch {
      setListError('Failed to delete configuration.');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActiveConfirm = async () => {
    if (!toggleStatusConfig) return;
    const llm = toggleStatusConfig;
    setTogglingId(llm.id);
    setToggleStatusConfig(null);
    try {
      await llmConfigurationService.setActive(llm.id, !llm.isActive);
      await fetchList();
    } catch {
      setListError('Failed to update status.');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">LLM Configurations</h2>
          <p className="text-sm text-gray-400">Manage all Large Language Model integrations and settings.</p>
        </div>
        <button onClick={() => { setEditConfig(null); setShowModal(true); }} className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm shadow">
          <PlusCircle className="w-4 h-4 mr-2" /> Add New LLM Configuration
        </button>
      </div>

      {listError && <p className="mb-3 text-sm text-red-400">{listError}</p>}

      <div className="overflow-x-auto border border-neutral-800 rounded">
        <table className="min-w-full text-sm text-left text-gray-300">
          <thead className="bg-neutral-900 text-gray-400">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Provider</th>
              <th className="px-4 py-2">Model</th>
              <th className="px-4 py-2">Temperature</th>
              <th className="px-4 py-2">Created On</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>
            ) : llmsList.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">No LLM configurations yet. Add one to get started.</td></tr>
            ) : (
              llmsList.map((llm) => (
                <tr key={llm.id} className="border-t border-neutral-800 hover:bg-neutral-800/30">
                  <td className="px-4 py-3">{getDisplayName(llm)}</td>
                  <td className="px-4 py-3">{providerNames[llm.provider] ?? llm.provider}</td>
                  <td className="px-4 py-3">{llm.modelName ?? '—'}</td>
                  <td className="px-4 py-3">{llm.temperature ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs">{llm.createdAt ? new Date(llm.createdAt).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setToggleStatusConfig(llm)}
                      disabled={togglingId === llm.id}
                      className={`inline-block px-2 py-1 rounded text-xs font-medium cursor-pointer disabled:opacity-50 ${llm.isActive ? 'bg-green-700 text-green-100 hover:bg-green-600' : 'bg-red-700 text-red-100 hover:bg-red-600'}`}
                      title={llm.isActive ? 'Set inactive' : 'Set active'}
                    >
                      {togglingId === llm.id ? '…' : llm.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3 flex justify-end gap-2">
                    <button
                      className="text-indigo-400 hover:text-indigo-300 transition-colors"
                      title="Manage Permissions & Access"
                      onClick={() => setAssignmentModal(true)}
                    >
                      <UserCog className="w-4 h-4" />
                    </button>
                    <button className="text-yellow-400 hover:text-white" title="Edit" onClick={() => handleEdit(llm)}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button className="text-red-400 hover:text-red-200" title="Delete" onClick={() => setDeleteId(llm.id)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <AddLLMConfigModal
          onClose={handleCloseModal}
          onSubmit={fetchList}
          initialData={editConfig}
        />
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-neutral-900 rounded-xl border border-neutral-800 p-6 max-w-sm w-full">
            <h3 className="text-white font-semibold mb-2">Delete configuration?</h3>
            <p className="text-gray-400 text-sm mb-4">This cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="text-gray-400 hover:text-white text-sm" disabled={deleting}>
                Cancel
              </button>
              <button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50" disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toggleStatusConfig && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-neutral-900 rounded-xl border border-neutral-800 p-6 max-w-sm w-full">
            <h3 className="text-white font-semibold mb-2">
              {toggleStatusConfig.isActive ? 'Set configuration to inactive?' : 'Set configuration to active?'}
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              {toggleStatusConfig.isActive
                ? 'This configuration will be disabled and will not be available for use until you activate it again.'
                : 'This configuration will be enabled and available for use.'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setToggleStatusConfig(null)}
                className="text-gray-400 hover:text-white text-sm"
                disabled={togglingId === toggleStatusConfig.id}
              >
                Cancel
              </button>
              <button
                onClick={handleToggleActiveConfirm}
                className={toggleStatusConfig.isActive ? 'bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50' : 'bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50'}
                disabled={togglingId === toggleStatusConfig.id}
              >
                {togglingId === toggleStatusConfig.id ? 'Updating…' : toggleStatusConfig.isActive ? 'Yes, set to Inactive' : 'Yes, set to Active'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignmentModal && (
        <PermissionsAssignmentModal
          onClose={() => setAssignmentModal(false)}
          onSave={() => {}}
          entityType="llm-configuration"
          entityName="LLM Configuration"
        />
      )}
    </div>
  );
};

export default LLMs;
